/**
 * useWizardPersistence - SessionStorage persistence for wizard state
 * Saves wizard state on every change, restores on mount, clears on completion
 */

import { useEffect, useRef } from 'react';
import type { WizardState } from '../contexts/WizardContext';
import { logger } from '../utils/logger';

const STORAGE_KEY = 'fmeastudio_wizard_state';

/**
 * Validates that a restored state object is valid
 */
function isValidWizardState(state: unknown): state is WizardState {
  if (typeof state !== 'object' || state === null) {
    return false;
  }

  // Type-narrow to record for property access
  const obj = state as Record<string, unknown>;

  // Check required fields
  if (typeof obj.isActive !== 'boolean') return false;
  if (typeof obj.currentStep !== 'string') return false;

  // Validate currentStep is one of the valid values
  const validSteps = [
    'welcome',
    'template-select',
    'bom-upload',
    'scratch-details',
    'copy-select',
    'review',
    'creating',
  ];
  if (!validSteps.includes(obj.currentStep)) {
    return false;
  }

  // Validate selectedPath if present
  if (obj.selectedPath !== null && obj.selectedPath !== undefined) {
    const validPaths = ['template', 'bom', 'scratch', 'copy'];
    if (typeof obj.selectedPath !== 'string' || !validPaths.includes(obj.selectedPath)) {
      return false;
    }
  }

  return true;
}

/**
 * Prepares state for serialization (removes non-serializable fields)
 */
function prepareStateForStorage(state: WizardState): Partial<WizardState> {
  const { bomFile, ...serializableState } = state;
  // Don't persist File objects - they can't be serialized
  return serializableState;
}

export interface UseWizardPersistenceOptions {
  /** Called when state is restored from storage */
  onRestore?: (state: WizardState) => void;
}

/**
 * Hook to manage wizard state persistence in sessionStorage
 */
export function useWizardPersistence(
  state: WizardState,
  options: UseWizardPersistenceOptions = {}
) {
  const { onRestore } = options;
  const isFirstRender = useRef(true);

  // Restore state on mount
  useEffect(() => {
    if (!isFirstRender.current) return;
    isFirstRender.current = false;

    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (!saved) return;

      const parsedState = JSON.parse(saved);

      // Validate before restoring
      if (!isValidWizardState(parsedState)) {
        logger.warn('Invalid wizard state in sessionStorage, ignoring');
        sessionStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Only restore if wizard was active
      if (parsedState.isActive) {
        onRestore?.(parsedState);
      } else {
        // Clear if wizard was not active
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      logger.error('Failed to restore wizard state:', error);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [onRestore]);

  // Save state to sessionStorage on every change
  useEffect(() => {
    // Skip on first render (restoration phase)
    if (isFirstRender.current) return;

    try {
      if (state.isActive) {
        // Save state if wizard is active
        const serializableState = prepareStateForStorage(state);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(serializableState));
      } else {
        // Clear state if wizard is not active (completed or cancelled)
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      // Handle quota exceeded errors gracefully
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        logger.error('SessionStorage quota exceeded, clearing wizard state');
        sessionStorage.removeItem(STORAGE_KEY);
      } else {
        logger.error('Failed to save wizard state:', error);
      }
    }
  }, [state]);

  // Cleanup on unmount (optional, sessionStorage persists anyway)
  useEffect(() => {
    return () => {
      // Could add cleanup logic here if needed
    };
  }, []);
}

/**
 * Manually clear wizard state from storage (useful for testing)
 */
export function clearWizardState(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Get current wizard state from storage (useful for debugging)
 */
export function getStoredWizardState(): WizardState | null {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (!saved) return null;

    const parsedState = JSON.parse(saved);
    return isValidWizardState(parsedState) ? parsedState : null;
  } catch {
    return null;
  }
}
